// web/src/admin/AdminLifecycle.tsx — /admin/lifecycle (portal spec 22 §3 "The queues"), three blocks top to bottom:
//   1. Ready to go live: sold/finishing sites with account + plan done. Start go-live (spec 14's route) once nameservers
//      are done; otherwise "Nameservers: not yet", a disabled button and Re-check (spec 14's ?refresh=1).
//   2. Payment failed: paused sites (paused_reason payment_failed): days paused, when the site goes off, the client's
//      email and phone, Turn site off now / Turn site back on (spec 25, 409 while SITE_OFF_ENABLED is off), Open in Stripe.
//   3. Tasks: spec 25's lifecycle_tasks that are open, running or failed (Cancel open, Retry failed, Attach an unmatched
//      prepare to a site), plus done/canceled from the last 7 days, collapsed.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { AdminSiteDetail, LifecycleTask } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Modal } from '../components/Modal';
import { Skeleton } from '../components/Skeleton';
import { StartGoLiveButton } from './StartGoLiveButton';
import { Btn, Collapsible, Empty, Section, ago, fmtDate, inputCls, useAction } from './adminUi';

const KIND: Record<LifecycleTask['kind'], string> = {
  prepare: 'Prepare', start_golive: 'Start go-live', payment_failed: 'Payment failed', payment_recovered: 'Payment recovered',
  site_off: 'Site off', site_on: 'Site on', cancel: 'Cancel', offboard: 'Offboard', archive_demo: 'Archive demo',
};
const STATUS_TONE: Record<LifecycleTask['status'], string> = { open: 'text-run', running: 'text-info', failed: 'text-danger', done: 'text-success', canceled: 'text-muted' };

/** One line from a task's payload (never the whole jsonb). */
function payloadLine(t: LifecycleTask): string {
  const p = t.payload ?? {};
  const bits: string[] = [];
  for (const k of ['businessName', 'email', 'reason', 'dueAt', 'after', 'amountCents', 'note'] as const) {
    const v = p[k];
    if (v === undefined || v === null || v === '') continue;
    bits.push(k === 'dueAt' ? `due ${fmtDate(String(v), true)}` : k === 'amountCents' ? `$${(Number(v) / 100).toLocaleString('en-US')}` : String(v));
  }
  if (p.forced) bits.push('forced');
  return bits.join(' · ');
}
const errorOf = (t: LifecycleTask) => String(t.payload?.error ?? t.payload?.note ?? t.payload?.lastError ?? '');
/** A phone from the site's sale-alert contact (E.164, or JSON { phone }) or the lead. */
function phoneOf(d: AdminSiteDetail): string | null {
  const c = d.site.orderAlertContact ?? '';
  try { const j = JSON.parse(c) as { phone?: string }; if (j.phone) return j.phone; } catch { /* not JSON */ }
  if (/^\+?[0-9 ()-]{7,}$/.test(c)) return c;
  return d.lead?.phone ?? null;
}

export function AdminLifecycle() {
  const { busy, run } = useAction();
  const ready = useQuery({ queryKey: ['admin-ready'], queryFn: () => api.adminReady() });
  const board = useQuery({ queryKey: ['admin-board'], queryFn: () => api.adminBoard() });
  const lists = useQueries({ queries: (['open', 'running', 'failed', 'done', 'canceled'] as const).map((status) => ({
    queryKey: ['admin-lifecycle', status], queryFn: () => api.adminLifecycle(status),
  })) });
  const sitesQ = useQuery({ queryKey: ['admin-sites'], queryFn: () => api.adminSites() });
  const pausedCards = (board.data ?? []).find((s) => s.stage === 'paused')?.cards ?? [];
  const pausedDetails = useQueries({ queries: pausedCards.map((c) => ({ queryKey: ['admin-site', c.slug], queryFn: () => api.adminSite(c.slug) })) });
  const failedPay = pausedDetails.map((d) => d.data).filter((d): d is AdminSiteDetail => !!d && d.site.pausedReason === 'payment_failed');

  const [rechecking, setRechecking] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ title: string; body: string; label: string; destructive?: boolean; go: () => Promise<unknown> } | null>(null);
  const [attach, setAttach] = useState<LifecycleTask | null>(null);

  const active = useMemo(() => [...(lists[0]!.data ?? []), ...(lists[1]!.data ?? []), ...(lists[2]!.data ?? [])]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt)), [lists]);
  const week = Date.now() - 7 * 86_400_000;
  const recent = [...(lists[3]!.data ?? []), ...(lists[4]!.data ?? [])].filter((t) => Date.parse(t.doneAt ?? t.createdAt) >= week)
    .sort((a, b) => (b.doneAt ?? b.createdAt).localeCompare(a.doneAt ?? a.createdAt));

  const recheck = async (slug: string) => {
    setRechecking(slug);
    await run(`recheck:${slug}`, () => api.adminOnboarding(slug, true), (o) => (o.nameservers === 'todo' ? 'Nameservers: not yet.' : 'Nameservers: done.'), 'Couldn’t check right now.');
    setRechecking(null);
  };
  const ask = (c: NonNullable<typeof confirm>) => setConfirm(c);

  return (
    <>
      <PageHeader title="Lifecycle" subtitle="Who's ready to go live, payments that failed, and what the go-live brain is doing." />
      <div className="flex min-w-0 flex-col gap-4">
        <Section title="Ready to go live">
          {ready.isLoading && <Skeleton variant="row" count={2} />}
          {ready.data && ready.data.length === 0 && <p className="text-sm text-muted">Nobody yet: a site shows here once the client has an account and a plan.</p>}
          <div className="flex flex-col gap-3">
            {(ready.data ?? []).map((r) => (
              <div key={r.slug} className="min-w-0 rounded-md border border-hair p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link to={`/admin/clients/${encodeURIComponent(r.slug)}`} className="inline-flex min-h-11 items-center break-words font-medium text-ink underline">{r.name}</Link>
                    <div className="text-xs text-muted">{r.slug} · {r.stage}{r.ticks.stripe === 'todo' ? ' · payments not set up (doesn’t block)' : ''}</div>
                  </div>
                  <span className={`text-sm ${r.nameservers ? 'text-success' : 'text-warn'}`}>Nameservers: {r.nameservers ? (r.ticks.nameservers === 'not_needed' ? 'not needed' : 'done') : 'not yet'}</span>
                </div>
                {!r.nameservers && r.ticks.nameserverInfo && (
                  <p className="mt-2 break-words text-xs text-muted">For the call: {r.ticks.nameserverInfo.nameServers.join(', ')}
                    {r.ticks.nameserverInfo.registrar ? ` at ${r.ticks.nameserverInfo.registrar}` : ''}</p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <StartGoLiveButton slug={r.slug} ready={r.nameservers} stage={r.stage} />
                  {!r.nameservers && <Btn icon="refresh" disabled={rechecking === r.slug} onClick={() => void recheck(r.slug)}>{rechecking === r.slug ? 'Checking…' : 'Re-check'}</Btn>}
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Payment failed">
          {board.isLoading && <Skeleton variant="row" count={1} />}
          {board.data && failedPay.length === 0 && pausedDetails.every((d) => !d.isLoading) && <p className="text-sm text-muted">No failed payments.</p>}
          <div className="flex flex-col gap-3">
            {failedPay.map((d) => {
              const owner = d.users.find((u) => u.role === 'owner') ?? d.users[0];
              const phone = phoneOf(d);
              const cus = d.org?.stripeCustomerId;
              return (
                <div key={d.site.slug} className="min-w-0 rounded-md border border-danger/40 p-3">
                  <Link to={`/admin/clients/${encodeURIComponent(d.site.slug)}`} className="inline-flex min-h-11 items-center break-words font-medium text-ink underline">{d.site.name}</Link>
                  <p className="mt-1 text-sm text-danger">{d.card.next?.label}</p>
                  <p className="mt-1 text-xs text-muted">{d.switchedOff ? `Off since ${fmtDate(d.switchedOff.offAt, true)}` : d.site.siteOffAt ? `Goes off ${fmtDate(d.site.siteOffAt, true)} (only with site switching on)` : 'No switch-off planned'}</p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    {owner && <a className="inline-flex min-h-11 items-center break-all text-ink underline" href={`mailto:${owner.email}`}>{owner.email}</a>}
                    {phone && <a className="inline-flex min-h-11 items-center text-ink underline" href={`tel:${phone.replace(/[^0-9+]/g, '')}`}>{phone}</a>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Btn kind="danger" disabled={!!busy} onClick={() => ask({ title: `Turn ${d.site.name} off now?`, body: 'Visitors see the “taking a short break” page until the card is fixed or you turn it back on.', label: 'Turn site off', destructive: true, go: () => run('off', () => api.adminSiteOff(d.site.slug), 'Site switched off.') })}>Turn site off now</Btn>
                    <Btn disabled={!!busy} onClick={() => ask({ title: `Turn ${d.site.name} back on?`, body: 'The site comes back for visitors right away.', label: 'Turn site on', go: () => run('on', () => api.adminSiteOn(d.site.slug), 'Site switched back on.') })}>Turn site back on</Btn>
                    {cus && <a className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink" href={`https://dashboard.stripe.com/customers/${cus}`} target="_blank" rel="noopener noreferrer">Open in Stripe</a>}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        <Section title="Tasks">
          {lists.some((l) => l.isLoading) && <Skeleton variant="row" count={2} />}
          {!lists.some((l) => l.isLoading) && active.length === 0 && <p className="text-sm text-muted">Nothing open, running or failed.</p>}
          <div className="flex flex-col gap-2">
            {active.map((t) => <TaskRow key={t.id} t={t} busy={busy} onCancel={() => ask({ title: `Cancel this ${KIND[t.kind]} task?`, body: 'The go-live brain won’t pick it up.', label: 'Cancel task', destructive: true, go: () => run(`c:${t.id}`, () => api.adminCancelTask(t.id), 'Task canceled.') })}
              onRetry={() => void run(`r:${t.id}`, () => api.adminRetryLifecycleTask(t.id), 'Task is open again.')} onAttach={() => setAttach(t)} />)}
          </div>
          <div className="mt-3">
            <Collapsible title="Done or canceled in the last 7 days" count={recent.length}>
              <div className="flex flex-col gap-2">{recent.map((t) => <TaskRow key={t.id} t={t} busy={busy} />)}</div>
            </Collapsible>
          </div>
        </Section>
      </div>

      <ConfirmDialog open={!!confirm} title={confirm?.title ?? ''} body={confirm?.body ?? ''} confirmLabel={confirm?.label ?? 'OK'} destructive={confirm?.destructive}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) void c.go(); }} onCancel={() => setConfirm(null)} />
      {attach && <AttachDialog task={attach} sites={(sitesQ.data ?? []).filter((s) => ['demo', 'sold', 'finishing'].includes(s.stage))} onClose={() => setAttach(null)} />}
    </>
  );
}

function TaskRow({ t, busy, onCancel, onRetry, onAttach }: { t: LifecycleTask; busy: string | null; onCancel?: () => void; onRetry?: () => void; onAttach?: () => void }) {
  const line = payloadLine(t);
  const err = t.status === 'failed' ? errorOf(t) : '';
  return (
    <div className="min-w-0 rounded-md border border-hair p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-ink">{KIND[t.kind]}</span>
        <span className={`text-xs ${STATUS_TONE[t.status]}`}>{t.status} · {ago(t.createdAt)}</span>
      </div>
      <div className="mt-1 break-words text-xs text-muted">
        {t.slug ? <Link className="text-ink underline" to={`/admin/clients/${encodeURIComponent(t.slug)}`}>{t.siteName ?? t.slug}</Link> : 'No site yet'}
        {line ? ` · ${line}` : ''} · by {t.createdBy}
      </div>
      {err && <p className="mt-1 break-words text-xs text-danger">{err}</p>}
      {(onCancel || onRetry || onAttach) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {t.status === 'open' && onCancel && <Btn kind="danger" disabled={!!busy} onClick={onCancel}>Cancel</Btn>}
          {t.status === 'failed' && onRetry && <Btn disabled={!!busy} onClick={onRetry} icon="refresh">Retry</Btn>}
          {t.kind === 'prepare' && !t.siteId && t.status === 'open' && onAttach && <Btn kind="primary" onClick={onAttach}>Attach to a site</Btn>}
        </div>
      )}
    </div>
  );
}

function AttachDialog({ task, sites, onClose }: { task: LifecycleTask; sites: { slug: string; name: string; stage: string }[]; onClose(): void }) {
  const { busy, run } = useAction();
  const [slug, setSlug] = useState(sites[0]?.slug ?? '');
  return (
    <Modal open onClose={onClose} title="Attach this payment to a site">
      <p className="text-sm text-muted">{payloadLine(task) || 'An unmatched $5,000 payment.'} Pick the site it paid for; Prepare then runs for it.</p>
      <label className="mt-3 flex flex-col gap-1.5 text-sm">
        <span className="font-medium text-ink">Site</span>
        <select value={slug} onChange={(e) => setSlug(e.target.value)} className={inputCls}>
          {sites.map((s) => <option key={s.slug} value={s.slug}>{s.name} ({s.slug}, {s.stage})</option>)}
        </select>
      </label>
      <div className="mt-4 flex justify-end gap-2">
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn kind="primary" disabled={!slug || !!busy} onClick={async () => { if (await run('attach', () => api.adminAttachTask(task.id, slug), 'Attached. Prepare runs for that site.')) onClose(); }}>Attach</Btn>
      </div>
    </Modal>
  );
}
